// Theme-aware Stripe Elements styling. Stripe's <Elements appearance> and the
// split-Card `style` prop do NOT accept CSS variables — they take literal color
// strings — so the exact hex values below are mirrored from the form's tokens in
// index.css (:root[data-theme="light"] and [data-theme="dark"]). Do not invent
// colors here; keep them in sync with those token blocks.
import { useEffect, useState } from 'react'

// Mirrored verbatim from index.css token blocks.
const T = {
  light: {
    teal: '#45BDA6',
    bg: '#ffffff', // --surface-bg
    inputBg: '#f9f9f7', // current Payment Element .Input bg
    text: '#1a1a1a', // --text-1
    textSecondary: '#333333', // --text-2
    label: 'rgba(0,0,0,0.55)', // current .Label
    placeholder: '#888888', // --input-placeholder
    iconColor: 'rgba(0,0,0,0.4)',
    border: 'rgba(0,0,0,0.12)', // current .Input border
    tabBg: '#f9f9f7',
    danger: '#dc2626', // current colorDanger
    focusRing: 'rgba(69,189,166,0.15)',
    elementsTheme: 'stripe',
  },
  dark: {
    teal: '#45BDA6',
    bg: '#1a1a1a', // --surface-bg
    inputBg: '#1e1e1e', // --input-bg
    text: '#e8e8e8', // --text-1 / --input-text
    textSecondary: '#b0b0b0', // --text-2
    label: '#888888', // --color-text-label
    placeholder: '#666666', // --input-placeholder
    iconColor: '#b0b0b0',
    border: '#333333', // --input-border
    tabBg: '#1a1a1a',
    danger: '#ef5350', // dark .stripe-modal-error
    focusRing: 'rgba(69,189,166,0.30)', // --input-focus-ring (dark)
    elementsTheme: 'night',
  },
}

function readTheme() {
  if (typeof document === 'undefined') return 'light'
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light'
}

/** Live theme ('dark'|'light'), re-rendering on <html data-theme> changes so a
 *  toggle while a modal is open re-themes (and re-keys) the Stripe Elements. */
export function useStripeTheme() {
  const [theme, setTheme] = useState(readTheme)
  useEffect(() => {
    const obs = new MutationObserver(() => setTheme(readTheme()))
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    setTheme(readTheme())
    return () => obs.disconnect()
  }, [])
  return theme
}

const FONT = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif'

/** `style`/options for the split Card elements (CardNumber/Expiry/Cvc). */
export function splitCardElementStyle(theme) {
  const c = T[theme === 'dark' ? 'dark' : 'light']
  return {
    style: {
      base: {
        color: c.text,
        fontFamily: FONT,
        fontSize: '15px',
        fontWeight: '400',
        iconColor: c.iconColor,
        backgroundColor: 'transparent',
        '::placeholder': { color: c.placeholder },
        ':-webkit-autofill': { color: c.text, backgroundColor: 'transparent' },
      },
      invalid: { color: c.danger, iconColor: c.danger },
    },
    disableLink: true,
  }
}

/** <Elements options.appearance> for the split-Card modal. */
export function splitElementsAppearance(theme) {
  const c = T[theme === 'dark' ? 'dark' : 'light']
  return {
    theme: c.elementsTheme,
    variables: {
      colorPrimary: c.teal,
      colorBackground: c.inputBg,
      colorText: c.text,
      colorDanger: c.danger,
      fontFamily: FONT,
      borderRadius: '8px',
    },
  }
}

/** <Elements options.appearance> for the Payment Element modal (card + bank). */
export function paymentElementAppearance(theme) {
  const c = T[theme === 'dark' ? 'dark' : 'light']
  return {
    theme: c.elementsTheme,
    variables: {
      colorPrimary: c.teal,
      colorBackground: theme === 'dark' ? c.inputBg : c.bg,
      colorText: c.text,
      colorTextSecondary: c.textSecondary,
      colorTextPlaceholder: c.placeholder,
      colorDanger: c.danger,
      fontFamily: 'system-ui, -apple-system, sans-serif',
      borderRadius: '8px',
      spacingUnit: '4px',
    },
    rules: {
      '.Label': { color: c.label, fontWeight: '500', fontSize: '13px' },
      '.Input': {
        backgroundColor: c.inputBg,
        color: c.text,
        borderColor: c.border,
        padding: '12px 14px',
        fontSize: '15px',
      },
      '.Input:focus': {
        borderColor: c.teal,
        boxShadow: `0 0 0 3px ${c.focusRing}`,
        backgroundColor: c.inputBg,
      },
      '.Input::placeholder': { color: c.placeholder },
      '.Tab': { backgroundColor: c.tabBg, borderColor: c.border, color: c.textSecondary },
      '.Tab:hover': { borderColor: c.teal, color: c.text },
      '.Tab--selected': {
        borderColor: c.teal,
        backgroundColor: 'rgba(69,189,166,0.10)',
        color: c.text,
      },
      '.TabIcon': { fill: c.textSecondary },
      '.Tab--selected .TabIcon': { fill: c.teal },
      '.PickerItem': { backgroundColor: c.inputBg, color: c.text, borderColor: c.border },
      '.PickerItem--selected': { backgroundColor: 'rgba(69,189,166,0.10)', borderColor: c.teal },
      '.Block': { backgroundColor: c.bg, borderColor: c.border },
    },
  }
}
